import React, { useCallback, useContext, useEffect, useState } from 'react';
import { BASE_URL_API } from '../../References/URLs';
import ActiveStoreContext from '../../store/ActiveStoreContext';
import AuthContext from '../../store/auth-context';
import styles from './CollectionReport.module.css';
import { Margin, Resolution, usePDF } from 'react-to-pdf';
import { BiCalendar, BiLoaderAlt } from 'react-icons/bi';
import DateRangeSelector from '../../Components/UI/DateRangeSelector';
import { convertToAmPm } from '../../Helper/calculations';
import { FaToggleOff, FaToggleOn } from "react-icons/fa";
import { MdFileDownload } from "react-icons/md";
import jsPDF from "jspdf";
import autoTable from 'jspdf-autotable';
import * as XLSX from "xlsx";
import CollectionReportDownloadOptions from './CollectionReportDownloadOptions';

const toLocalIsoDate = (date) => (
    new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toISOString().substring(0, 10)
)

const getDefaultDateRange = () => {
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - 0)

    return {
        startDate: toLocalIsoDate(start),
        endDate: toLocalIsoDate(end)
    }
}

const CollectionReport = () => {
    const authCtx = useContext(AuthContext);
    const asCtx = useContext(ActiveStoreContext);
    const activeStore = (() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== "")
                ? JSON.parse(asCtx.activeStore)
                : null;
        } catch {
            return null;
        }
    })();
    const allowedActions = ((d => Array.isArray(d?.featuresData) ? d.featuresData : [])((() => { try { return typeof authCtx?.loginData === "string" ? JSON.parse(authCtx.loginData) : authCtx?.loginData ?? {}; } catch { return {}; } })()))
        .filter(a => a?.storeIdentifier === activeStore?.storeIdentifier)
        .map(a => a?.feature)
        .filter(Boolean);
    console.log("this is allowed actions", allowedActions)

    const [isFetchingData, setIsFetchingData] = useState(false)
    const isGstAvailable = !!String(activeStore?.gstNumber || "").trim();
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)
    const closeModal = () => { setIsDatePickerOpen(false) }
    const [includeServiceItems, setIncludeServiceItems] = useState(true);
    const [dates, setDates] = useState(getDefaultDateRange)
    const [isMoreOpen, setIsMoreOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [storeServicesCategories, setStoreServicesCategories] = useState([])
    const [categoryFilter, setCategoryFilter] = useState('')
    const changeDateHandler = useCallback((startDate, endDate) => {
        setDates((prevDates) => {
            if (prevDates.startDate === startDate && prevDates.endDate === endDate) {
                return prevDates
            }

            return {
                startDate,
                endDate
            }
        })
    }, [])
    const [includeExpenses, setIncludeExpenses] = useState(false)
    const [collectionReport, setCollectionReport] = useState(null)
    const [genderFilter, setGenderFilter] = useState('')
    // Replace your downloadPdfHandler function with this:

    const downloadPdfHandler = async () => {
        if (!collectionReport) {
            alert('No data available to generate PDF');
            return;
        }

        setLoading(true);

        try {
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 8;
            const contentWidth = pageWidth - (margin * 2);
            const leftSpan = isGstAvailable ? 6 : 5


            let yPosition = margin;

            // ============================================
            // HEADER SECTION
            // ============================================
            pdf.setFontSize(24);
            pdf.setTextColor(0, 0, 0);
            pdf.setFont(undefined, 'bold');
            pdf.text('Collection Report', pageWidth / 2, yPosition, { align: 'center' });
            yPosition += 8;

            pdf.setFontSize(11);
            pdf.setTextColor(51, 51, 51);
            pdf.setFont(undefined, 'normal');
            pdf.text(activeStore?.storeName || '', pageWidth / 2, yPosition, { align: 'center' });
            yPosition += 5;

            pdf.setFontSize(10);
            const address = activeStore?.storeAddress || '';
            const lines = pdf.splitTextToSize(address, pageWidth - 40);

            pdf.text(
                lines,
                pageWidth / 2,
                yPosition,
                {
                    align: 'center'
                }
            );

            yPosition += (lines.length * 5); // adjust height after text


            pdf.text(new Date().toString(), pageWidth / 2, yPosition, { align: 'center' });
            yPosition += 12;

            // ============================================
            // PAYMENT COLLECTION DETAILS TABLE
            // ============================================
            pdf.setFontSize(14);
            pdf.setFont(undefined, 'bold');
            pdf.setTextColor(0, 0, 0);
            pdf.text('Payment Collection Details', margin, yPosition);
            yPosition += 8;

            const transactions = collectionReport?.allTransactions || [];

            if (transactions.length > 0) {
                const transactionRows = [];

                transactions.forEach((item) => {
                    // Main transaction row
                    const gstAmount = isGstAvailable
                        ? parseFloat((item?.amountToBeCollected || 0) * (5 / 105)).toFixed(1)
                        : null;

                    const baseRow = [
                        {
                            content: `${item?.invoiceNumber?.substr(18, 9)}`,
                            styles: { fontStyle: 'bold', textColor: [0, 0, 0] }
                        },
                        item?.payStatusLastUpdatedAt?.substr(0, 10) || '',
                        item?.payStatusLastUpdatedAt ? convertToAmPm(item?.payStatusLastUpdatedAt?.substr(11, 8), false) : '',
                        item?.client_name || '',
                        item?.mobile || '',
                        `${item?.amountToBeCollected || 0}`,
                    ];

                    // Insert GST column ONLY when needed
                    if (isGstAvailable) {
                        baseRow.push(`${gstAmount}`);
                    }

                    baseRow.push(
                        `${item?.collectionViaCard || 0}`,
                        `${item?.collectionViaQR || 0}`,
                        { content: `${item?.collectionViaCash || 0}`, styles: { align: 'right' } }
                    );

                    transactionRows.push(baseRow);


                    // Service items sub-rows
                    if (includeServiceItems) {
                        if (item?.allItemsEachBooking && item.allItemsEachBooking.length > 0) {
                            item.allItemsEachBooking.forEach((itemCart) => {
                                transactionRows.push([
                                    {
                                        content: `${itemCart?.serviceName || ''}`,
                                        colSpan: leftSpan,
                                        styles: {
                                            fontSize: 6,
                                            textColor: [85, 85, 85],
                                            cellPadding: { top: 1, right: 1, bottom: 1, left: 8 }
                                        }
                                    },
                                    {
                                        content: `x ${itemCart?.quantity || 0}`,
                                        colSpan: 1,
                                        styles: {
                                            fontSize: 6,
                                            cellPadding: { top: 1, right: 1, bottom: 1, left: 10 }
                                        }
                                    },
                                    {
                                        content: `${itemCart?.totalDiscPrice || 0}`,
                                        colSpan: 3,
                                        styles: { fontSize: 6, cellPadding: { top: 1, right: 1, bottom: 1, left: 5 }, align: 'right' }
                                    }
                                ]);
                            });
                        }
                    }

                });

                autoTable(pdf, {
                    startY: yPosition,
                    head: [[
                        'BILL No',
                        'DATE',
                        'TIME',
                        'NAME',
                        'MOBILE',
                        'AMT (Rs.)',
                        ...(isGstAvailable ? ['GST (Rs.)'] : []),
                        'CARD (Rs.)',
                        'QR (Rs.)',
                        'CASH (Rs.)'
                    ]],
                    body: transactionRows,
                    theme: 'plain',
                    styles: {
                        fontSize: 8,
                        cellPadding: { top: 1, right: 1, bottom: 1, left: 2 },
                        lineColor: [212, 213, 213],
                        lineWidth: 0.1,
                    },
                    headStyles: {
                        fillColor: [238, 242, 246],
                        textColor: [0, 0, 0],
                        fontStyle: 'bold',
                        fontSize: 8,
                        halign: 'left',
                        cellPadding: { top: 1, right: 1, bottom: 1, left: 2 },
                    },
                    alternateRowStyles: {
                        fillColor: [255, 255, 255]
                    },
                    columnStyles: {
                        0: { cellWidth: 18 },
                        1: { cellWidth: 19 },
                        2: { cellWidth: 16 },
                        3: { cellWidth: 30 },
                        4: { cellWidth: 20 },
                        5: { cellWidth: 21 },

                        ...(isGstAvailable ? { 6: { cellWidth: 16 } } : {}),

                        ...(isGstAvailable
                            ? {
                                7: { cellWidth: 18 },
                                8: { cellWidth: 18 },
                                9: { cellWidth: 18 },
                            }
                            : {
                                6: { cellWidth: 22 },
                                7: { cellWidth: 22 },
                                8: { cellWidth: 22 },
                            }
                        )
                    },
                    margin: { left: margin, right: margin },
                    didDrawPage: (data) => {
                        const pageCount = pdf.internal.getNumberOfPages();
                        pdf.setFontSize(8);
                        pdf.setTextColor(128);
                        pdf.text(
                            `Page ${pdf.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`,
                            pageWidth / 2,
                            pageHeight - 10,
                            { align: 'center' }
                        );
                    }
                });

                yPosition = pdf.lastAutoTable.finalY + 12;
            }

            // ============================================
            // SUMMARY SECTION
            // ============================================
            const checkPageSpace = (requiredSpace) => {
                if (yPosition + requiredSpace > pageHeight - 20) {
                    pdf.addPage();
                    yPosition = margin;
                }
            };

            checkPageSpace(80);

            const totalNormalCollection = parseFloat(
                parseFloat(collectionReport?.collectionViaCard || 0) +
                parseFloat(collectionReport?.collectionViaCash || 0) +
                parseFloat(collectionReport?.collectionViaQr || 0)
            );
            const totalOlderCollection = parseFloat(collectionReport?.olderPayTotalAmount || 0);
            const totalExpenses = parseFloat(collectionReport?.totalExpense || 0);
            const totalCollection = totalNormalCollection + totalOlderCollection;
            const finalCollection = includeExpenses ? totalCollection - totalExpenses : totalCollection;

            const summaryData = [
                ['Total Sales:', `Rs. ${totalCollection.toFixed(2)}`]
            ];

            if (isGstAvailable) {
                summaryData.push(
                    ['Total GST as per sales:', `Rs. ${parseFloat(parseFloat(collectionReport?.totalCollection) * (5 / 105)).toFixed(2)}`]
                )
            }
            summaryData.push(
                ['Total Normal Collection:', `Rs. ${totalNormalCollection.toFixed(2)}`],
                ['Total Older Collection:', `+ Rs. ${totalOlderCollection.toFixed(2)}`],
                ['Total Refund:', '- Rs. 0.00']
            )

            if (includeExpenses) {
                summaryData.push([
                    'Total Expenses:',
                    `${totalExpenses < 0 ? '- ' : ''}Rs. ${Math.abs(totalExpenses).toFixed(2)}`
                ]);
            }

            summaryData.push([
                includeExpenses ? 'Total Amount Collection After Expenses:' : 'Total Amount Collection:',
                `Rs. ${finalCollection.toFixed(2)}`
            ]);

            autoTable(pdf, {
                startY: yPosition,
                body: summaryData,
                theme: 'plain',
                styles: {
                    fontSize: 10,
                    cellPadding: 4,
                    fontStyle: 'bold',
                    textColor: [85, 85, 85]
                },
                columnStyles: {
                    0: { cellWidth: contentWidth * 0.6, halign: 'left' },
                    1: { cellWidth: contentWidth * 0.4, halign: 'right' }
                },
                margin: { left: margin, right: margin },
                didParseCell: (data) => {
                    if (data.row.index === summaryData.length - 1) {
                        data.cell.styles.lineWidth = { top: 0.5, bottom: 0.5 };
                        data.cell.styles.lineColor = [223, 223, 223];
                    }
                }
            });

            yPosition = pdf.lastAutoTable.finalY + 12;

            // ============================================
            // PAYMENT MODE TABLES
            // ============================================
            checkPageSpace(60);

            const tableStartY = yPosition;
            const tableWidth = (contentWidth - 10) / 3;

            // Left Table - Older Payment
            autoTable(pdf, {
                startY: tableStartY,
                head: [['Mode of Collection', 'Amount(Older Payment)']],
                body: [
                    ['Card', `${parseFloat(collectionReport?.olderPayCollectionViaCard || 0).toFixed(2)}`],
                    ['Cash', `${parseFloat(collectionReport?.olderPayCollectionViaCash || 0).toFixed(2)}`],
                    ['QR', `${parseFloat(collectionReport?.olderPayCollectionViaQr || 0).toFixed(2)}`],
                    ['Total', `${totalOlderCollection.toFixed(2)}`]
                ],
                theme: 'plain',
                styles: {
                    fontSize: 9,
                    cellPadding: 3,
                    lineColor: [2, 127, 255],
                    lineWidth: 0.5,
                },
                headStyles: {
                    fillColor: [255, 255, 255],
                    textColor: [0, 0, 0],
                    fontStyle: 'bold',
                    halign: 'left'
                },
                columnStyles: {
                    0: { cellWidth: tableWidth * 0.5 },
                    1: { cellWidth: tableWidth * 0.5, halign: 'right' }
                },
                margin: { left: margin },
                tableWidth: tableWidth,
                didParseCell: (data) => {
                    if (data.row.index === 3 && data.section === 'body') {
                        data.cell.styles.fontSize = 10;
                        data.cell.styles.fontStyle = 'bold';
                    }
                }
            });

            // Middle Table - Current Payment
            autoTable(pdf, {
                startY: tableStartY,
                head: [['Mode of Collection', 'Amount (Current)']],
                body: [
                    ['Card', `Rs. ${parseFloat(collectionReport?.collectionViaCard || 0).toFixed(2)}`],
                    ['Cash', `Rs. ${parseFloat(collectionReport?.collectionViaCash || 0).toFixed(2)}`],
                    ['QR', `Rs. ${parseFloat(collectionReport?.collectionViaQr || 0).toFixed(2)}`],
                    ['Total', `Rs. ${totalNormalCollection.toFixed(2)}`]
                ],
                theme: 'plain',
                styles: {
                    fontSize: 9,
                    cellPadding: 3,
                    lineColor: [2, 127, 255],
                    lineWidth: 0.5,
                },
                headStyles: {
                    fillColor: [255, 255, 255],
                    textColor: [0, 0, 0],
                    fontStyle: 'bold',
                    halign: 'left'
                },
                columnStyles: {
                    0: { cellWidth: tableWidth * 0.5 },
                    1: { cellWidth: tableWidth * 0.5, halign: 'right' }
                },
                margin: { left: margin + tableWidth + 5 },
                tableWidth: tableWidth,
                didParseCell: (data) => {
                    if (data.row.index === 3 && data.section === 'body') {
                        data.cell.styles.fontSize = 10;
                        data.cell.styles.fontStyle = 'bold';
                    }
                }
            });

            // Right Table - Summary
            const netCashCollection = (
                (parseFloat(collectionReport?.collectionViaCash) || 0) +
                (parseFloat(collectionReport?.olderPayCollectionViaCash) || 0) -
                (parseFloat(collectionReport?.totalExpenseViaCash) || 0)
            );

            const rightTableBody = [
                ['Total Collection (Older + Current)', `Rs. ${totalCollection.toFixed(2)}`],
                includeExpenses
                    ? ['Total Expenses', `Rs. ${totalExpenses.toFixed(2)}`]
                    : ['-', '-'],
                ['Total Bills', (collectionReport?.totalTransactions || 0).toString()],
                [
                    'Net Cash Collection',
                    `${netCashCollection < 0 ? '- ' : ''}Rs. ${Math.abs(netCashCollection).toFixed(2)}`
                ],
                ['Net Collection', `Rs. ${finalCollection.toFixed(2)}`]
            ];

            autoTable(pdf, {
                startY: tableStartY,
                body: rightTableBody,
                theme: 'plain',
                styles: {
                    fontSize: 9,
                    cellPadding: 3,
                    lineColor: [2, 127, 255],
                    lineWidth: 0.5,
                },
                headStyles: {
                    fillColor: [255, 255, 255],
                    textColor: [0, 0, 0],
                    fontStyle: 'bold',
                    minCellHeight: 0,
                    cellPadding: 0
                },
                columnStyles: {
                    0: { cellWidth: tableWidth * 0.55, fontStyle: 'bold' },
                    1: { cellWidth: tableWidth * 0.45, halign: 'right', fontStyle: 'bold' }
                },
                margin: { left: margin + (tableWidth * 2) + 10 },
                tableWidth: tableWidth,
                didParseCell: (data) => {
                    if (data.row.index === 4 && data.section === 'body') {
                        data.cell.styles.fillColor = [0, 0, 0, 0.2];
                        data.cell.styles.fontSize = 10;
                        data.cell.styles.fontStyle = 'bold';
                        data.cell.styles.textColor = finalCollection >= 0 ? [0, 128, 0] : [255, 0, 0];
                    }
                }
            });

            // ============================================
            // SAVE PDF
            // ============================================
            const fileName = `collection_report_${dates.startDate}_to_${dates.endDate}.pdf`;
            pdf.save(fileName);

        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Failed to generate PDF. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const downloadExcelHandler = () => {
        if (!collectionReport) {
            alert("No data available");
            return;
        }

        const workbook = XLSX.utils.book_new();

        // -------------------------------------------
        // 1️⃣ Transactions Sheet
        // -------------------------------------------
        const sheetData = [];

        // Build dynamic header
        const header = [
            "BILL No",
            "DATE",
            "TIME",
            "NAME",
            "MOBILE",
            "AMOUNT",
            "CARD",
            "QR",
            "CASH"
        ];

        if (isGstAvailable) header.push("GST");    // ADD GST COLUMN

        sheetData.push(header);


        const transactions = collectionReport?.allTransactions || [];

        transactions.forEach(item => {
            const mainRow = [
                `${item?.invoiceNumber?.substr(18, 9)}`,
                item?.payStatusLastUpdatedAt?.substr(0, 10) || "",
                item?.payStatusLastUpdatedAt
                    ? convertToAmPm(item?.payStatusLastUpdatedAt.substr(11, 8), false)
                    : "",
                item?.client_name || "",
                item?.mobile || "",

                Number(item?.amountToBeCollected || 0),
                Number(item?.collectionViaCard || 0),
                Number(item?.collectionViaQR || 0),
                Number(item?.collectionViaCash || 0)
            ];

            if (isGstAvailable) {
                mainRow.push(Number(parseFloat(parseFloat(item?.amountToBeCollected) * (5 / 105)).toFixed(1) || 0));   // GST value
            }

            sheetData.push(mainRow);

            // ----------------------------------------------------
            // Service sub-rows (if enabled)
            // ----------------------------------------------------
            if (includeServiceItems && item?.allItemsEachBooking?.length) {
                item.allItemsEachBooking.forEach(cart => {
                    const subRow = [
                        `   ${cart.serviceName}`,
                        "",
                        "",
                        "",
                        "",

                        Number(cart.totalDiscPrice || 0),
                        "",
                        "",
                        ""
                    ];

                    if (isGstAvailable) {
                        subRow.push(""); // Subrows do NOT show GST
                    }

                    sheetData.push(subRow);
                });
            }
        });


        const sheet1 = XLSX.utils.aoa_to_sheet(sheetData);
        XLSX.utils.book_append_sheet(workbook, sheet1, "Transactions");

        // -------------------------------------------
        // 2️⃣ Summary Sheet
        // -------------------------------------------
        const summarySheet = XLSX.utils.aoa_to_sheet([
            ["Summary"],
            [],
            ["Total Sales", collectionReport.totalCollection],
            ["Final GST as per total sales", Number(parseFloat(collectionReport?.totalCollection * (5 / 105)).toFixed(2))],
            ["Total Normal Collection", collectionReport.collectionViaCard + collectionReport.collectionViaCash + collectionReport.collectionViaQr],
            ["Total Older Collection", collectionReport.olderPayTotalAmount],
            ["Total Refund", 0],
            ["Total Expenses", collectionReport.totalExpense],
            ["Final Collection", collectionReport.totalCollection - (includeExpenses ? collectionReport.totalExpense : 0)]
        ]);

        XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

        // -------------------------------------------
        // 3️⃣ Save File
        // -------------------------------------------
        XLSX.writeFile(
            workbook,
            `collection_report_${dates.startDate}_to_${dates.endDate}.xlsx`
        );
    };

    const downloadCsvHandler = () => {
        if (!collectionReport) {
            alert("No data available");
            return;
        }

        // -------------------------------------------
        // 1️ CSV Header
        // -------------------------------------------
        let csv = "";

        if (isGstAvailable) {
            csv += "BILL No,DATE,TIME,NAME,MOBILE,AMOUNT,GST,CARD,QR,CASH\n";
        } else {
            csv += "BILL No,DATE,TIME,NAME,MOBILE,AMOUNT,CARD,QR,CASH\n";
        }

        // -------------------------------------------
        // 2️ Transactions
        // -------------------------------------------
        const transactions = collectionReport?.allTransactions || [];

        transactions.forEach(item => {

            const gstValue = Number(parseFloat(parseFloat(item?.amountToBeCollected) * (5 / 105)).toFixed(2) || 0);

            const row = [
                `${item?.invoiceNumber?.substr(18, 9)}`,
                item?.payStatusLastUpdatedAt?.substr(0, 10) || "",
                item?.payStatusLastUpdatedAt
                    ? convertToAmPm(item?.payStatusLastUpdatedAt.substr(11, 8), false)
                    : "",
                `"${item?.client_name || ""}"`,
                `"${item?.mobile || ""}"`,
                Number(item?.amountToBeCollected || 0)
            ];

            if (isGstAvailable) {
                row.push(gstValue);
            }

            row.push(
                Number(item?.collectionViaCard || 0),
                Number(item?.collectionViaQR || 0),
                Number(item?.collectionViaCash || 0)
            );

            csv += row.join(",") + "\n";

            // -------------------------------------------
            // 3️ Sub-rows (service items)
            // -------------------------------------------
            if (includeServiceItems) {
                if (item?.allItemsEachBooking?.length) {
                    item.allItemsEachBooking.forEach(cart => {

                        const subRow = [
                            `"   ${cart.serviceName.replace(/"/g, '""')}"`,
                            "", // date
                            "", // time
                            "", // name
                            "", // mobile
                            Number(cart.totalDiscPrice || 0)
                        ];

                        if (isGstAvailable) {
                            subRow.push(""); // empty GST column
                        }

                        subRow.push("", "", "", ""); // card, qr, cash

                        csv += subRow.join(",") + "\n";
                    });
                }
            }
        });

        // -------------------------------------------
        // 4️ Trigger Download
        // -------------------------------------------
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `collection_report_${dates.startDate}_to_${dates.endDate}.csv`;
        link.click();
    };

    const getCollectionReport = async () => {
        setIsFetchingData(true)
        const getCollectionReportResponse = await fetch(BASE_URL_API + "getCollectionReport",
            {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    startDate: dates.startDate,
                    endDate: dates.endDate,
                    categoryFilter: categoryFilter,
                    genderFilter: genderFilter
                })
            });

        if (!getCollectionReportResponse.ok) {
            console.log("Something went wrong : Server Error")
        } else {
            const getCollectionReportRespo = await getCollectionReportResponse.json()
            if (getCollectionReportRespo.status === "success") {
                setCollectionReport(getCollectionReportRespo?.response)
            } else {
                if (getCollectionReportRespo.message === "tokenExpired") {
                    authCtx.logout()
                } else {
                    setCollectionReport(null)
                }
            }
            console.log(getCollectionReportRespo)
        }
        setIsFetchingData(false)
    }

    const downloadExcelForGst = () => {
        if (!collectionReport) {
            alert("No data available");
            return;
        }

        const workbook = XLSX.utils.book_new();

        // -------------------------------------------
        // 1️⃣ Transactions Sheet
        // -------------------------------------------
        const sheetData = [];

        // Build dynamic header
        const header = [
            "BILL No",
            "DATE",
            "TIME",
            "NAME",
            "MOBILE",
            "AMOUNT",
            "CARD",
            "QR",
            "CASH"
        ];

        if (isGstAvailable) header.push("GST");    // ADD GST COLUMN

        sheetData.push(header);


        const transactions = collectionReport?.allTransactions || [];

        transactions.forEach(item => {
            const mainRow = [
                `${item?.invoiceNumber?.substr(18, 9)}`,
                item?.payStatusLastUpdatedAt?.substr(0, 10) || "",
                item?.payStatusLastUpdatedAt
                    ? convertToAmPm(item?.payStatusLastUpdatedAt.substr(11, 8), false)
                    : "",
                item?.client_name || "",
                item?.mobile || "",

                Number(item?.amountToBeCollected || 0),
                Number(item?.collectionViaCard || 0),
                Number(item?.collectionViaQR || 0),
                Number(item?.collectionViaCash || 0)
            ];

            if (isGstAvailable) {
                mainRow.push(Number(parseFloat(parseFloat(item?.amountToBeCollected) * (5 / 105)).toFixed(1) || 0));   // GST value
            }

            sheetData.push(mainRow);

            // ----------------------------------------------------
            // Service sub-rows (if enabled)
            // ----------------------------------------------------
            if (includeServiceItems && item?.allItemsEachBooking?.length) {
                item.allItemsEachBooking.forEach(cart => {
                    const subRow = [
                        `   ${cart.serviceName}`,
                        "",
                        "",
                        "",
                        "",

                        Number(cart.totalDiscPrice || 0),
                        "",
                        "",
                        ""
                    ];

                    if (isGstAvailable) {
                        subRow.push(""); // Subrows do NOT show GST
                    }

                    sheetData.push(subRow);
                });
            }
        });


        const sheet1 = XLSX.utils.aoa_to_sheet(sheetData);
        XLSX.utils.book_append_sheet(workbook, sheet1, "Transactions");

        // -------------------------------------------
        // 2️⃣ Summary Sheet
        // -------------------------------------------
        const summarySheet = XLSX.utils.aoa_to_sheet([
            ["Summary"],
            [],
            ["Total Sales", collectionReport.totalCollection],
            ["Final GST as per total sales", Number(parseFloat(collectionReport?.totalCollection * (5 / 105)).toFixed(2))],
            ["Total Normal Collection", collectionReport.collectionViaCard + collectionReport.collectionViaCash + collectionReport.collectionViaQr],
            ["Total Older Collection", collectionReport.olderPayTotalAmount],
            ["Total Refund", 0],
            ["Total Expenses", collectionReport.totalExpense],
            ["Final Collection", collectionReport.totalCollection - (includeExpenses ? collectionReport.totalExpense : 0)]
        ]);

        XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

        // -------------------------------------------
        // 3️⃣ Save File
        // -------------------------------------------
        XLSX.writeFile(
            workbook,
            `collection_report_${dates.startDate}_to_${dates.endDate}.xlsx`
        );
    }

    useEffect(() => {
        getCollectionReport()
    }, [dates?.startDate, dates?.endDate, categoryFilter, genderFilter])


    // function filterTransactions(transactions, filters) {
    //     const { categoryIdentifier, genderIdentifier } = filters;

    //     return transactions.filter(transaction =>
    //         transaction.allItemsEachBooking.some(item => {
    //             const categoryMatch = categoryIdentifier ? item.categoryIdentifier === categoryIdentifier : true;
    //             const genderMatch = genderIdentifier ? item.genderIdentifier === genderIdentifier : true;
    //             return categoryMatch && genderMatch;
    //         })
    //     );
    // }

    // // Example usage:
    // const filters = {
    //     categoryIdentifier: "SS5617987264051728285009F42EBE6394630E74",
    //     genderIdentifier: "", // Leave empty or undefined if no filter is needed
    // };

    // const filteredTransactions = filterTransactions(collectionReport?.allTransactions, filters);

    const getStoreServicesCategories = async () => {
        const getStoreServicesCategoriesResponse = await fetch(BASE_URL_API + "getStoreServicesCategories",
            {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier
                })
            });

        if (!getStoreServicesCategoriesResponse.ok) {
            console.log("Something went wrong : Server Error")
        } else {
            const getStoreServicesCategoriesRespo = await getStoreServicesCategoriesResponse.json();
            if (getStoreServicesCategoriesRespo?.status === "success") {
                setStoreServicesCategories(getStoreServicesCategoriesRespo?.response)
            } else {
                if (getStoreServicesCategoriesRespo?.message === "token Expired, please login again!") {
                    authCtx.logout()
                } else {
                    setStoreServicesCategories([])
                }
            }
        }
    }

    useEffect(() => {
        getStoreServicesCategories()
    }, [])




    if (activeStore === null) {
        return (
            <div>
                Please select a store first!
            </div>
        )
    } else {
        return (
            <div className={styles.masterContainer}>
                <div className={styles.crTitle} >
                    <div className={styles.downloadActionsWrapper}>
                        <button className={styles.includeExpenses} onClick={() => setIncludeServiceItems(prev => !prev)} >
                            {includeServiceItems === true ? <FaToggleOn size={22} /> : <FaToggleOff size={22} />} Include Services
                        </button>
                        <button className={styles.downloadPdfBtn} onClick={() => downloadPdfHandler()} ><MdFileDownload size={22} /> PDF</button>
                        <button className={styles.downloadPdfBtn} onClick={() => downloadCsvHandler()} ><MdFileDownload size={22} /> CSV</button>
                        <button className={styles.downloadPdfBtn} onClick={() => downloadExcelHandler()} ><MdFileDownload size={22} /> EXCEL</button>
                        <div className={styles.toggleBtn}>
                            <button onClick={() => setIncludeExpenses(!includeExpenses)} className={styles.includeExpenses} >
                                {includeExpenses === true ? <FaToggleOn size={22} /> : <FaToggleOff size={22} />} Include Expenses
                            </button>
                            <select onChange={(e) => setCategoryFilter(e.target.value)}>
                                <option value="">Select Category</option>
                                {storeServicesCategories?.map((item, index) => (
                                    <option value={item?.categoryIdentifier} key={index} >{item?.categoryLabel}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className={styles.downloadActionsWrapperRight}>
                        {allowedActions?.includes("collection-reports_dateFilter") &&
                            <div className={styles.dateWrapper}>
                                <div className={styles.dateRangeSelectorWrapperBtn}
                                    onClick={() => setIsDatePickerOpen(true)}
                                >
                                    {dates.startDate === dates.endDate ?
                                        <div>
                                            {dates.startDate} &nbsp;
                                        </div>
                                        :
                                        <div>
                                            From {dates.startDate} to {dates.endDate} &nbsp;
                                        </div>
                                    }

                                    <BiCalendar />
                                </div>
                                <DateRangeSelector
                                    isOpen={isDatePickerOpen}
                                    closeModal={closeModal}
                                    changeDateHandler={changeDateHandler}
                                    initialStartDate={dates.startDate}
                                    initialEndDate={dates.endDate}
                                />

                            </div>
                        }
                        <div>
                            <button className={styles.moreActionBtn} onClick={() => setIsMoreOpen(true)} >More</button>
                            <CollectionReportDownloadOptions isOpen={isMoreOpen}
                                collectionReport={collectionReport} isGstAvailable={isGstAvailable} dates={dates}
                                includeExpenses={includeExpenses}
                                includeServiceItems={includeServiceItems}
                                onClose={() => setIsMoreOpen(false)}
                            />
                        </div>
                    </div>
                </div>
                <div className={styles.mainWrapper} id="content-id">
                    <header className={styles.header} >
                        <h1>Collection Report</h1>
                        <p>{activeStore?.storeName}</p>
                        <p>{activeStore?.storeAddress}</p>
                        <p>{Date()}</p>
                    </header>

                    {isFetchingData === true ?
                        <section className={styles.patientDetails}>
                            <h2>Payment Collection Details</h2>
                            <div className={styles.detailsRow}>
                                <span>BILL No</span>
                                <span>DATE</span>
                                <span>PAYMENT TIME</span>
                                <span>CLIENT NAME</span>
                                <span>MOBILE</span>
                                <span>AMOUNT RECEIVED</span>
                                <span>VIA CARD</span>
                                <span>VIA QR</span>
                                <span>VIA CASH</span>
                                {isGstAvailable && <span>GST</span>}
                            </div>
                            <div className={styles.loadingDataWrapper}>
                                <div>
                                    <BiLoaderAlt className={styles.spinner} size={30} />
                                </div>
                                <div className={styles.loadingTxt} >
                                    Loading data, please wait...
                                </div>
                            </div>
                        </section>
                        :
                        <section className={styles.patientDetails}>
                            <h2>Payment Collection Details</h2>
                            <div className={styles.detailsRow}>
                                <span>BILL No</span>
                                <span>DATE</span>
                                <span>PAYMENT TIME</span>
                                <span>CLIENT NAME</span>
                                <span>MOBILE</span>
                                <span>AMOUNT RECEIVED</span>
                                <span>VIA CARD</span>
                                <span>VIA QR</span>
                                <span>VIA CASH</span>
                                {isGstAvailable && <span>GST</span>}
                            </div>
                            {collectionReport?.allTransactions?.map((item, index) => (
                                <div key={index}>
                                    <div className={styles.detailsRow} key={index}>
                                        <span>{item?.invoiceNumber?.substr(18, 9)}</span>
                                        <span>{item?.payStatusLastUpdatedAt?.substr(0, 10)}</span>
                                        <span>{convertToAmPm(item?.payStatusLastUpdatedAt?.substr(11, 8), false)}</span>
                                        <span>{item?.client_name}</span>
                                        <span>{item?.mobile}</span>
                                        <span>Rs. {item?.amountToBeCollected}</span>
                                        <span>Rs. {item?.collectionViaCard}</span>
                                        <span>Rs. {item?.collectionViaQR}</span>
                                        <span>Rs. {item?.collectionViaCash}</span>
                                        {isGstAvailable && <span>Rs. {parseFloat(parseFloat(item?.amountToBeCollected) * (5 / 105)).toFixed(1)}</span>}
                                    </div>
                                    {includeServiceItems &&
                                        <div className={styles.cartItemWrapper}>
                                            {item?.allItemsEachBooking?.map((itemCart, indexCart) => (
                                                <div key={indexCart} className={styles.eachCartItem}>
                                                    <span className={styles.serviceName}>{itemCart?.serviceName}</span>
                                                    <span className={styles.categoryName}>{itemCart?.categoryName}</span>
                                                    <span>{itemCart?.quantity}</span>
                                                    <span>{itemCart?.totalDiscPrice}</span>
                                                </div>
                                            ))}
                                        </div>
                                    }
                                </div>
                            ))}
                        </section>
                    }

                    <section className={styles.summarySection}>
                        <div className={styles.summaryRow}>
                            <span>Total Sales :</span>
                            <span>Rs. {isNaN(parseFloat(collectionReport?.totalCollection)) ? '0.00' : parseFloat(collectionReport?.totalCollection).toFixed(2)}</span>
                        </div>
                        {isGstAvailable &&
                            <div className={styles.summaryRow}>
                                <span>Total GST as per sales :</span>
                                <span>Rs. {Number(parseFloat(collectionReport?.totalCollection) * (5 / 105) || 0).toFixed(2)}</span>
                            </div>
                        }
                        <div className={styles.summaryRow}>
                            <span>Total Normal Collection:</span>
                            <span>Rs. {parseFloat(parseFloat(collectionReport?.collectionViaCard || 0) + parseFloat(collectionReport?.collectionViaCash || 0) + parseFloat(collectionReport?.collectionViaQr || 0)).toFixed(2)}</span>
                        </div>
                        <div className={styles.summaryRow}>
                            <span>Total Older Collection:</span>
                            <span>Rs. {parseFloat(collectionReport?.olderPayTotalAmount || 0).toFixed(2)} </span>
                        </div>
                        <div className={styles.summaryRow}>
                            <span>Total Refund:</span>
                            <span>0.00</span>
                        </div>
                        {includeExpenses === true &&
                            <div className={styles.summaryRow}>
                                <span>Total Expenses:</span>
                                <span className={styles.dangerTxt}>
                                    {parseFloat(collectionReport?.totalExpense || 0) < 0 ? '-' : ''}
                                    Rs. {Math.abs(parseFloat(collectionReport?.totalExpense || 0)).toFixed(2)}
                                </span>
                            </div>
                        }
                        {includeExpenses === true ?
                            <div className={`${styles.summaryRow} ${styles.borderTop}`}>
                                <span>Total Amount Collection After Expenses:</span>
                                <span>
                                    Rs. {
                                        (() => {
                                            const tnc = parseFloat(
                                                parseFloat(collectionReport?.collectionViaCard || 0) +
                                                parseFloat(collectionReport?.collectionViaCash || 0) +
                                                parseFloat(collectionReport?.collectionViaQR || 0)
                                            );

                                            const topc = parseFloat(collectionReport?.olderPayTotalAmount || 0);
                                            const te = parseFloat(collectionReport?.totalExpense || 0).toFixed(2);
                                            const tfc = tnc + topc - te;

                                            return tfc.toFixed(2); // Ensure the final result is displayed properly
                                        })()
                                    }
                                </span>
                            </div>
                            :
                            <div className={`${styles.summaryRow} ${styles.borderTop}`}>
                                <span>Total Amount Collection :</span>
                                <span>
                                    Rs. {
                                        (() => {
                                            const tnc = parseFloat(
                                                parseFloat(collectionReport?.collectionViaCard || 0) +
                                                parseFloat(collectionReport?.collectionViaCash || 0) +
                                                parseFloat(collectionReport?.collectionViaQr || 0)
                                            );

                                            const topc = parseFloat(collectionReport?.olderPayTotalAmount || 0);
                                            const tfc = tnc + topc;

                                            return tfc.toFixed(2); // Ensure the final result is displayed properly
                                        })()
                                    }
                                </span>
                            </div>
                        }


                    </section>

                    <div className={styles.tableWrapper}>
                        <div className={styles.leftTableWrapper}>
                            <div className={`${styles.eachRowWrapper} ${styles.erwHeader}`}>
                                <div>Mode of Collection</div>
                                <div>Amount(Older Payment)</div>
                            </div>
                            <div className={`${styles.eachRowWrapper} ${styles.erwOnly}`}>
                                <div>Card</div>
                                <div>Rs. {parseFloat(collectionReport?.olderPayCollectionViaCard || 0).toFixed(2)}</div>
                            </div>
                            <div className={`${styles.eachRowWrapper} ${styles.erwOnly}`}>
                                <div>Cash</div>
                                <div>Rs. {parseFloat(collectionReport?.olderPayCollectionViaCash || 0).toFixed(2)}</div>
                            </div>
                            <div className={`${styles.eachRowWrapper} ${styles.erwOnly}`}>
                                <div>QR</div>
                                <div>Rs. {parseFloat(collectionReport?.olderPayCollectionViaQr || 0).toFixed(2)}</div>
                            </div>
                            <div className={`${styles.eachRowWrapper} ${styles.erwOnly} ${styles.eachTableTotal}`}>
                                <div>Total</div>
                                <div>Rs. {parseFloat(collectionReport?.olderPayTotalAmount || 0).toFixed(2)}</div>
                            </div>

                        </div>
                        <div className={styles.leftTableWrapper}>
                            <div className={`${styles.eachRowWrapper} ${styles.erwHeader}`}>
                                <div>Mode of Collection</div>
                                <div>Amount (Current Payment)</div>
                            </div>
                            <div className={`${styles.eachRowWrapper} ${styles.erwOnly}`}>
                                <div>Card</div>
                                <div>Rs. {collectionReport?.collectionViaCard}</div>
                            </div>
                            <div className={`${styles.eachRowWrapper} ${styles.erwOnly}`}>
                                <div>Cash</div>
                                <div>Rs. {collectionReport?.collectionViaCash}</div>
                            </div>
                            <div className={`${styles.eachRowWrapper} ${styles.erwOnly}`}>
                                <div>QR</div>
                                <div>Rs. {collectionReport?.collectionViaQr}</div>
                            </div>
                            <div className={`${styles.eachRowWrapper} ${styles.erwOnly} ${styles.eachTableTotal}`}>
                                <div>Total</div>
                                {/* <div>Rs. {collectionReport?.totalCollection}</div> */}
                                <div>Rs. {parseFloat(collectionReport?.collectionViaCard || 0) + parseFloat(collectionReport?.collectionViaCash || 0) + parseFloat(collectionReport?.collectionViaQr || 0)} </div>
                            </div>
                        </div>

                        <div className={styles.rightTableWrapper}>
                            <div className={`${styles.eachRowWrapper} ${styles.erwHeader}`}>
                                <div>Total Collection (Older + Current)</div>
                                {/* <div>Rs. {(parseFloat(collectionReport?.olderPayTotalAmount || 0) + parseFloat(collectionReport?.totalCollection || 0)).toFixed(2)}</div> */}
                                <div>Rs. {
                                    (() => {
                                        const tnc = parseFloat(
                                            parseFloat(collectionReport?.collectionViaCard || 0) +
                                            parseFloat(collectionReport?.collectionViaCash || 0) +
                                            parseFloat(collectionReport?.collectionViaQr || 0)
                                        );

                                        const topc = parseFloat(collectionReport?.olderPayTotalAmount || 0);
                                        const tfc = tnc + topc;

                                        return tfc.toFixed(2); // Ensure the final result is displayed properly
                                    })()
                                }</div>
                            </div>
                            {includeExpenses === true ?
                                <div className={`${styles.eachRowWrapper} ${styles.erwOnly}`}>
                                    <div>Total Expenses</div>
                                    <div>Rs. {(parseFloat(collectionReport?.totalExpense || 0)).toFixed(2)}</div>
                                </div>
                                :
                                <div className={`${styles.eachRowWrapper} ${styles.erwOnly}`}>
                                    <div>-</div>
                                    <div>-</div>
                                </div>
                            }
                            <div className={`${styles.eachRowWrapper} ${styles.erwOnly}`}>
                                <div>Total Bills</div>
                                <div>{collectionReport?.totalTransactions}</div>
                            </div>
                            <div className={`${styles.eachRowWrapper} ${styles.erwOnly}`}>
                                <div>Net Cash Collection</div>
                                <div>
                                    {
                                        ((parseFloat(collectionReport?.collectionViaCash) || 0) +
                                            (parseFloat(collectionReport?.olderPayCollectionViaCash) || 0) -
                                            (parseFloat(collectionReport?.totalExpenseViaCash) || 0)
                                        ) < 0 ? '-' : ''
                                    } Rs.
                                    {
                                        Math.abs(
                                            (parseFloat(collectionReport?.collectionViaCash) || 0) +
                                            (parseFloat(collectionReport?.olderPayCollectionViaCash) || 0) -
                                            (parseFloat(collectionReport?.totalExpenseViaCash) || 0)
                                        ).toFixed(2)
                                    }
                                </div>


                            </div>
                            <div className={`${styles.eachRowWrapper}  ${styles.netCollection}`}>
                                <div>Net Collection</div>
                                <div
                                    className={
                                        (parseFloat(collectionReport?.totalCollection || 0) - parseFloat(collectionReport?.totalExpense || 0)) >= 0
                                            ? styles.positive
                                            : styles.negative
                                    }
                                >
                                    {
                                        (parseFloat(collectionReport?.totalCollection || 0) - parseFloat(collectionReport?.totalExpense || 0)) < 0
                                            ? '-'
                                            : ''
                                    }
                                    {includeExpenses === true ?
                                        <span>
                                            Rs. {
                                                (() => {
                                                    const tnc = parseFloat(
                                                        parseFloat(collectionReport?.collectionViaCard || 0) +
                                                        parseFloat(collectionReport?.collectionViaCash || 0) +
                                                        parseFloat(collectionReport?.collectionViaQr || 0)
                                                    );

                                                    const topc = parseFloat(collectionReport?.olderPayTotalAmount || 0);
                                                    const te = parseFloat(collectionReport?.totalExpense || 0).toFixed(2);
                                                    const tfc = tnc + topc - te;

                                                    return tfc.toFixed(2); // Ensure the final result is displayed properly
                                                })()
                                            }
                                        </span>
                                        :
                                        <span>
                                            Rs. {
                                                (() => {
                                                    const tnc = parseFloat(
                                                        parseFloat(collectionReport?.collectionViaCard || 0) +
                                                        parseFloat(collectionReport?.collectionViaCash || 0) +
                                                        parseFloat(collectionReport?.collectionViaQr || 0)
                                                    );

                                                    const topc = parseFloat(collectionReport?.olderPayTotalAmount || 0);
                                                    const te = parseFloat(collectionReport?.totalExpense || 0).toFixed(2);
                                                    const tfc = tnc + topc;

                                                    return tfc.toFixed(2); // Ensure the final result is displayed properly
                                                })()
                                            }
                                        </span>
                                    }

                                    {/* Rs. {Math.abs(parseFloat(collectionReport?.totalCollection || 0) - parseFloat(collectionReport?.totalExpense || 0)).toFixed(2)} */}
                                </div>


                            </div>
                        </div>

                    </div>

                </div>
            </div>
        );
    }
};

export default CollectionReport;
